import { cn } from '@/lib/utils/cn';

/**
 * T-0100 — Suggested-prompts chip row. Clickable chips that pre-fill
 * the chat input. Used by the empty state (initial 4 prompts) and
 * could be reused below the input when the conversation is empty.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * lines 599-603 — `<button class="chip" style="background:var(--paper-100);
 * border:1px solid var(--ink-200);cursor:pointer">…</button>`.
 */
export interface SuggestedPromptsProps {
  /** Prompt strings to render. Order is preserved. */
  prompts: readonly string[];
  /** Called with the chosen prompt's text when a chip is activated. */
  onPick: (prompt: string) => void;
  /** When true, all chips are inert (e.g. during a pending response). */
  disabled?: boolean;
  className?: string;
}

export function SuggestedPrompts({ prompts, onPick, disabled, className }: SuggestedPromptsProps) {
  if (prompts.length === 0) return null;
  return (
    <div
      className={cn('flex flex-wrap gap-2', className)}
      role="list"
      aria-label="Suggested questions"
    >
      {prompts.map((p) => (
        <button
          key={p}
          type="button"
          role="listitem"
          onClick={() => onPick(p)}
          disabled={disabled}
          className={cn(
            // Mirrors the prototype's `.chip` shell + paper-100 fill.
            'inline-flex h-[22px] items-center whitespace-nowrap rounded-chip',
            'border border-ink-200 bg-paper-100 px-2.5 text-[12px] font-medium text-ink-700',
            'hover:bg-paper-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'transition-colors',
          )}
        >
          {p}
        </button>
      ))}
    </div>
  );
}
